import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  BarChart3, Users, X, TrendingUp, ArrowDown, ArrowUp, Minus, Zap,
  Target, Star, AlertTriangle, Lightbulb, RefreshCw, Building2, Wallet,
  CheckCircle, XCircle, MapPin, Scale, Filter
} from "lucide-react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { API } from "../App";

// ============ SHARED COMPONENTS ============
const FreqBar = ({ items, color, max }) => (
  <div className="space-y-1.5">
    {items?.map((item) => (
      <div key={item.key || item.name} className="space-y-0.5">
        <div className="flex justify-between text-xs">
          <span className="text-slate-700 font-medium truncate mr-2">{item.name}</span>
          <span className="text-slate-500 flex-shrink-0">{item.count}{item.pct !== undefined ? ` (${item.pct}%)` : ""}</span>
        </div>
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
          <div className={`h-full rounded-full ${color}`} style={{ width: `${(item.count / (max || 1)) * 100}%` }} />
        </div>
      </div>
    ))}
  </div>
);

const KPI = ({ label, value, icon: Icon }) => (
  <Card>
    <CardContent className="p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0"><Icon className="w-5 h-5 text-indigo-600" /></div>
      <div><p className="text-2xl font-bold text-slate-900">{value}</p><p className="text-xs text-slate-500">{label}</p></div>
    </CardContent>
  </Card>
);

const FunnelTable = ({ data }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-xs">
      <thead><tr className="border-b">
        <th className="pb-2 text-left font-medium text-slate-600">Factor</th>
        <th className="pb-2 text-center font-medium text-red-600">Elim %</th>
        <th className="pb-2 text-center font-medium text-green-600">High %</th>
        <th className="pb-2 text-center font-medium text-orange-600">Med %</th>
        <th className="pb-2 text-center font-medium text-blue-600">Low %</th>
        <th className="pb-2 text-center font-medium text-slate-800">Avg Wt</th>
        <th className="pb-2 w-[100px]"></th>
      </tr></thead>
      <tbody>
        {data?.map((f) => (
          <tr key={f.key} className="border-b last:border-0">
            <td className="py-1.5 font-medium text-slate-700">{f.name}</td>
            <td className="py-1.5 text-center"><span className={f.eliminated_pct > 30 ? "text-red-700 font-bold" : "text-slate-500"}>{f.eliminated_pct}%</span></td>
            <td className="py-1.5 text-center"><span className={f.high_pct > 40 ? "text-green-700 font-bold" : "text-slate-500"}>{f.high_pct}%</span></td>
            <td className="py-1.5 text-center text-slate-500">{f.medium_pct}%</td>
            <td className="py-1.5 text-center text-slate-500">{f.low_pct}%</td>
            <td className="py-1.5 text-center font-bold">{f.avg_weight}</td>
            <td className="py-1.5"><div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-indigo-500" style={{ width: `${(f.avg_weight / 10) * 100}%` }} /></div></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ============ TAB: EXECUTIVE OVERVIEW ============
const OverviewTab = ({ data, trends, funnel }) => {
  if (!data) return null;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI label="Total Profiles" value={data.total_profiles} icon={Users} />
        <KPI label="Personas Active" value={data.persona_distribution?.length || 0} icon={Target} />
        <KPI label="Projects Evaluated" value={data.project_summary?.length || 0} icon={Building2} />
        <KPI label="Budget Ranges" value={data.budget_distribution?.length || 0} icon={Wallet} />
      </div>

      {/* Monthly Trend */}
      {trends?.profiles_trend?.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Profiles Over Time (Monthly)</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-end gap-1 h-32">
              {trends.profiles_trend.map((t) => {
                const maxC = Math.max(...trends.profiles_trend.map((x) => x.count), 1);
                return (
                  <div key={t.month} className="flex-1 flex flex-col items-center justify-end">
                    <span className="text-[9px] font-bold text-indigo-600 mb-0.5">{t.count || ""}</span>
                    <div className="w-full bg-indigo-500 rounded-t" style={{ height: `${(t.count / maxC) * 100}%`, minHeight: t.count > 0 ? "4px" : "0" }} />
                    <span className="text-[8px] text-slate-400 mt-1 rotate-0">{t.month.slice(5)}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step Funnel */}
      {funnel?.funnel?.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Completion Funnel ({funnel.total_started} started)</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-1">
              {funnel.funnel.filter((f) => f.reached > 0).map((f) => (
                <div key={f.step} className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-500 w-16 text-right">{f.label}</span>
                  <div className="flex-1 h-4 bg-slate-100 rounded overflow-hidden">
                    <div className="h-full bg-indigo-500 rounded" style={{ width: `${f.pct}%` }} />
                  </div>
                  <span className="text-xs font-medium text-slate-700 w-14 text-right">{f.reached} ({f.pct}%)</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Users className="w-4 h-4" /> Persona Distribution</CardTitle></CardHeader>
          <CardContent><div className="space-y-2">{data.persona_distribution?.map((p) => (
            <div key={p.key} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50"><span className="text-sm font-medium">{p.name}</span><div className="flex items-center gap-2"><span className="text-xs text-slate-500">{p.pct}%</span><span className="text-sm font-bold text-indigo-600">{p.count}</span></div></div>
          ))}</div></CardContent>
        </Card>
        <div className="space-y-4">
          <Card className="border-green-200"><CardHeader className="pb-2"><CardTitle className="text-sm text-green-700 flex items-center gap-2"><ArrowUp className="w-4 h-4" /> Top High Factors</CardTitle></CardHeader>
            <CardContent><FreqBar items={data.top_high_factors} color="bg-green-500" max={data.top_high_factors?.[0]?.count || 1} /></CardContent>
          </Card>
          <Card className="border-red-200"><CardHeader className="pb-2"><CardTitle className="text-sm text-red-700 flex items-center gap-2"><X className="w-4 h-4" /> Top Eliminated</CardTitle></CardHeader>
            <CardContent><FreqBar items={data.top_eliminated_factors} color="bg-red-400" max={data.top_eliminated_factors?.[0]?.count || 1} /></CardContent>
          </Card>
        </div>
      </div>

      {data.project_summary?.length > 0 && (
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Building2 className="w-4 h-4" /> Project Fit Summary</CardTitle></CardHeader>
          <CardContent><div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="border-b text-left"><th className="pb-2 text-slate-600">Project</th><th className="pb-2 text-center text-slate-600">Avg Fit</th><th className="pb-2 text-center text-slate-600">Evals</th><th className="pb-2 text-slate-600">Top Persona</th></tr></thead><tbody>
            {data.project_summary.map((p, i) => (<tr key={i} className="border-b last:border-0"><td className="py-2 font-medium">{p.name}</td><td className="py-2 text-center"><Badge className={p.avg_fit_score >= 70 ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>{p.avg_fit_score}%</Badge></td><td className="py-2 text-center text-slate-500">{p.evaluations}</td><td className="py-2 text-slate-600">{p.top_persona}</td></tr>))}
          </tbody></table></div></CardContent>
        </Card>
      )}
    </div>
  );
};

// ============ TAB: PERSONA INTELLIGENCE ============
const PersonaTab = ({ personas, city }) => {
  const [sel, setSel] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async (key) => {
    setSel(key); setLoading(true);
    const params = new URLSearchParams();
    if (key) params.set("persona", key);
    if (city) params.set("city", city);
    try { const r = await axios.get(`${API}/mhm/dashboard/persona?${params}`); setData(r.data); } catch {} finally { setLoading(false); }
  }, [city]);
  useEffect(() => { load(null); }, [load]);
  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" variant={!sel ? "default" : "outline"} onClick={() => load(null)}>All</Button>
        {personas?.map((p) => <Button key={p.key} size="sm" variant={sel === p.key ? "default" : "outline"} onClick={() => load(p.key)}>{p.name}</Button>)}
      </div>
      {loading && <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" /></div>}
      {data && !loading && (
        <div className="space-y-4">
          <div className="flex items-center gap-2"><h3 className="text-lg font-bold">{data.persona_name}</h3><Badge variant="outline">{data.total} profiles</Badge></div>
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Factor Funnel</CardTitle></CardHeader><CardContent><FunnelTable data={data.factor_funnel} /></CardContent></Card>
          <div className="grid md:grid-cols-2 gap-4">
            {data.pairwise_wins?.length > 0 && <Card><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Zap className="w-4 h-4" /> Pairwise Winners</CardTitle></CardHeader><CardContent><FreqBar items={data.pairwise_wins.map((w) => ({...w, count: w.wins}))} color="bg-indigo-500" max={data.pairwise_wins[0]?.wins || 1} /></CardContent></Card>}
            <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Scenario Responses</CardTitle></CardHeader><CardContent><div className="flex gap-3">{Object.entries(data.scenario_responses || {}).map(([k, v]) => (<div key={k} className="flex-1 p-2.5 rounded-lg bg-slate-50 border text-center"><p className="text-[10px] text-slate-500 capitalize">{k.replace(/_/g, " ")}</p><p className="text-lg font-bold">{v}</p></div>))}</div></CardContent></Card>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ TAB: FACTOR INTELLIGENCE ============
const FactorTab = ({ city }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const params = city ? `?city=${city}` : "";
    axios.get(`${API}/mhm/dashboard/factors${params}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [city]);
  if (loading) return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" /></div>;
  if (!data) return null;
  return (
    <div className="space-y-6">
      <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Factor Funnel (All Personas)</CardTitle></CardHeader><CardContent><FunnelTable data={data.factor_funnel} /></CardContent></Card>
      {data.group_analysis?.length > 0 && (
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Factor Group Analysis</CardTitle></CardHeader><CardContent>
          <div className="grid grid-cols-3 gap-4">{data.group_analysis.map((g) => (<div key={g.key} className="p-4 rounded-xl bg-slate-50 border text-center"><p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{g.name}</p><p className="text-2xl font-black mt-1">{g.avg_weight}</p><p className="text-xs text-slate-400">avg weight ({g.factor_count} factors)</p></div>))}</div>
        </CardContent></Card>
      )}
      {data.persona_heatmap?.length > 0 && data.persona_keys?.length > 0 && (
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Factor x Persona Heatmap (High Selection %)</CardTitle></CardHeader><CardContent>
          <div className="overflow-x-auto"><table className="w-full text-[10px]"><thead><tr className="border-b"><th className="pb-2 text-left font-medium text-slate-600">Factor</th>
            {data.persona_keys.map((pk) => <th key={pk} className="pb-2 text-center font-medium text-slate-600 px-1">{(data.persona_names?.[pk] || pk).split(" ").slice(0, 2).join(" ")}</th>)}
          </tr></thead><tbody>
            {data.persona_heatmap.map((row) => (<tr key={row.key} className="border-b last:border-0"><td className="py-1 text-xs font-medium text-slate-700">{row.name}</td>
              {data.persona_keys.map((pk) => { const v = row[pk] || 0; const bg = v >= 60 ? "bg-green-200 text-green-900" : v >= 30 ? "bg-green-50 text-green-700" : v > 0 ? "bg-slate-50 text-slate-500" : "text-slate-300"; return <td key={pk} className={`py-1 text-center font-medium ${bg} px-1`}>{v > 0 ? `${v}%` : "—"}</td>; })}
            </tr>))}
          </tbody></table></div>
        </CardContent></Card>
      )}
    </div>
  );
};

// ============ TAB: PROJECT-MARKET FIT ============
const ProjectFitTab = ({ city }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    const params = city ? `?city=${city}` : "";
    axios.get(`${API}/mhm/dashboard/project-fit${params}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [city]);
  if (loading) return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" /></div>;
  if (!data?.projects?.length) return <div className="text-center py-12 text-slate-500"><Building2 className="w-8 h-8 mx-auto mb-2 opacity-40" /><p>No project comparisons yet.</p></div>;
  const proj = selected ? data.projects.find((p) => p.name === selected) : null;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {data.projects.map((p) => (
          <button key={p.name} onClick={() => setSelected(p.name)} className={`p-3 rounded-xl border-2 text-left transition-all ${selected === p.name ? "border-indigo-400 bg-indigo-50" : "border-slate-200 hover:border-slate-300"}`}>
            <p className="text-sm font-bold">{p.name}</p><p className="text-xs text-slate-500">{p.location}</p>
            <div className="flex items-center justify-between mt-2"><Badge className={p.avg_fit_score >= 70 ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>{p.avg_fit_score}% fit</Badge><span className="text-[10px] text-slate-400">{p.evaluations} evals</span></div>
          </button>
        ))}
      </div>
      {proj && (
        <div className="space-y-4"><Separator /><h3 className="text-lg font-bold">{proj.name}</h3>
          {proj.persona_fit?.length > 0 && <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Fit by Persona</CardTitle></CardHeader><CardContent><div className="space-y-1.5">{proj.persona_fit.map((pf) => (<div key={pf.persona} className="flex items-center gap-2"><span className="text-xs w-[140px] truncate">{pf.name}</span><div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden"><div className={`h-full rounded-full ${pf.avg_fit >= 70 ? "bg-green-500" : "bg-amber-400"}`} style={{ width: `${pf.avg_fit}%` }} /></div><span className="text-xs font-bold w-10 text-right">{pf.avg_fit}%</span></div>))}</div></CardContent></Card>}
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Demand vs Supply Gap</CardTitle></CardHeader><CardContent>
            <div className="overflow-x-auto"><table className="w-full text-xs"><thead><tr className="border-b"><th className="pb-2 text-left font-medium">Factor</th><th className="pb-2 text-center font-medium text-indigo-600">Demand</th><th className="pb-2 text-center font-medium text-emerald-600">Supply</th><th className="pb-2 text-center font-medium">Gap</th></tr></thead><tbody>
              {proj.factor_gaps?.filter((f) => f.demand > 0).map((f) => (<tr key={f.key} className="border-b last:border-0"><td className="py-1.5 font-medium">{f.name}</td><td className="py-1.5 text-center">{f.demand}</td><td className="py-1.5 text-center">{f.supply}</td><td className="py-1.5 text-center"><span className={`font-bold ${f.gap > 2 ? "text-red-600" : f.gap > 0 ? "text-amber-600" : "text-green-600"}`}>{f.gap > 0 ? `+${f.gap}` : f.gap}</span></td></tr>))}
            </tbody></table></div>
          </CardContent></Card>
          <div className="grid md:grid-cols-2 gap-4">
            {proj.strengths?.length > 0 && <Card className="border-green-200"><CardHeader className="pb-2"><CardTitle className="text-sm text-green-700 flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Strengths</CardTitle></CardHeader><CardContent><div className="flex flex-wrap gap-1">{proj.strengths.map((s, i) => <Badge key={i} className="bg-green-50 text-green-700 border border-green-200">{s}</Badge>)}</div></CardContent></Card>}
            {proj.weaknesses?.length > 0 && <Card className="border-red-200"><CardHeader className="pb-2"><CardTitle className="text-sm text-red-700 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Weaknesses</CardTitle></CardHeader><CardContent><div className="flex flex-wrap gap-1">{proj.weaknesses.map((w, i) => <Badge key={i} className="bg-red-50 text-red-700 border border-red-200">{w}</Badge>)}</div></CardContent></Card>}
          </div>
        </div>
      )}
    </div>
  );
};

// ============ TAB: TRADEOFF LAB ============
const TradeoffTab = ({ city }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const params = city ? `?city=${city}` : "";
    axios.get(`${API}/mhm/dashboard/tradeoffs${params}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [city]);
  if (loading) return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" /></div>;
  if (!data || data.total === 0) return <div className="text-center py-12 text-slate-500"><Scale className="w-8 h-8 mx-auto mb-2 opacity-40" /><p>No tradeoff data yet.</p></div>;
  return (
    <div className="space-y-6">
      {/* Top Tradeoffs */}
      <Card><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Scale className="w-4 h-4" /> Top Tradeoffs</CardTitle></CardHeader><CardContent>
        <div className="space-y-2">
          {data.top_tradeoffs?.map((t, i) => (
            <div key={i} className="flex items-center gap-2 p-2.5 rounded-lg bg-slate-50 border">
              <span className={`text-xs font-bold flex-1 text-right ${t.a_pct > t.b_pct ? "text-green-700" : "text-slate-500"}`}>{t.factor_a_name}</span>
              <div className="w-32 flex h-4 rounded-full overflow-hidden bg-slate-200 flex-shrink-0">
                <div className="bg-green-500 flex items-center justify-center" style={{ width: `${t.a_pct}%` }}><span className="text-[8px] text-white font-bold">{t.a_pct}%</span></div>
                <div className="bg-slate-400 flex items-center justify-center" style={{ width: `${t.b_pct}%` }}><span className="text-[8px] text-white font-bold">{t.b_pct}%</span></div>
              </div>
              <span className={`text-xs font-bold flex-1 ${t.b_pct > t.a_pct ? "text-green-700" : "text-slate-500"}`}>{t.factor_b_name}</span>
              <span className="text-[10px] text-slate-400 w-8 text-right">{t.total}</span>
            </div>
          ))}
        </div>
      </CardContent></Card>

      {/* Pairwise Matrix */}
      {data.pairwise_matrix?.length > 0 && (
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Pairwise Matrix (Win Counts)</CardTitle></CardHeader><CardContent>
          <div className="overflow-x-auto"><table className="w-full text-[10px]"><thead><tr className="border-b">
            <th className="pb-2 text-left font-medium text-slate-600">Factor A</th><th className="pb-2 text-center font-medium">vs</th><th className="pb-2 text-left font-medium text-slate-600">Factor B</th>
            <th className="pb-2 text-center font-medium text-green-600">A Wins</th><th className="pb-2 text-center font-medium text-slate-600">B Wins</th><th className="pb-2 text-center font-medium">Winner</th>
          </tr></thead><tbody>
            {data.pairwise_matrix.map((p, i) => (<tr key={i} className="border-b last:border-0">
              <td className="py-1 font-medium">{p.factor_a_name}</td><td className="py-1 text-center text-slate-300">vs</td><td className="py-1 font-medium">{p.factor_b_name}</td>
              <td className="py-1 text-center font-bold text-green-600">{p.a_wins}</td><td className="py-1 text-center">{p.b_wins}</td>
              <td className="py-1 text-center"><Badge className="bg-indigo-50 text-indigo-700 text-[10px]">{p.winner_name}</Badge></td>
            </tr>))}
          </tbody></table></div>
        </CardContent></Card>
      )}

      {/* Scenario by Persona */}
      {Object.keys(data.scenario_by_persona || {}).length > 0 && (
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Scenario Responses by Persona</CardTitle></CardHeader><CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {Object.entries(data.scenario_by_persona).map(([pid, pd]) => (
              <div key={pid} className="p-3 rounded-lg bg-slate-50 border">
                <p className="text-xs font-semibold text-slate-800 mb-2">{pd.name}</p>
                <div className="space-y-1">{Object.entries(pd.responses).map(([k, v]) => (
                  <div key={k} className="flex justify-between text-[10px]"><span className="text-slate-500 capitalize">{k.replace(/_/g, " ")}</span><span className="font-bold">{v}</span></div>
                ))}</div>
              </div>
            ))}
          </div>
        </CardContent></Card>
      )}
    </div>
  );
};

// ============ TAB: CITY ANALYTICS ============
const CityTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  useEffect(() => { axios.get(`${API}/mhm/dashboard/cities`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  if (loading) return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" /></div>;
  if (!data?.cities?.length) return <div className="text-center py-12 text-slate-500"><MapPin className="w-8 h-8 mx-auto mb-2 opacity-40" /><p>No city data yet. Cities are captured during MhM lead capture.</p></div>;
  const sel = selected ? data.cities.find((c) => c.city === selected) : null;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {data.cities.map((c) => (
          <button key={c.city} onClick={() => setSelected(c.city)} className={`p-3 rounded-xl border-2 text-left transition-all ${selected === c.city ? "border-indigo-400 bg-indigo-50" : "border-slate-200 hover:border-slate-300"}`}>
            <div className="flex items-center gap-1.5 mb-1"><MapPin className="w-3.5 h-3.5 text-indigo-500" /><span className="text-sm font-bold text-slate-900">{c.city}</span></div>
            <div className="flex items-center justify-between"><span className="text-xs text-slate-500">{c.pct}% of total</span><span className="text-lg font-bold text-indigo-600">{c.count}</span></div>
          </button>
        ))}
      </div>
      {sel && (
        <div className="space-y-4"><Separator /><h3 className="text-lg font-bold flex items-center gap-2"><MapPin className="w-5 h-5 text-indigo-500" />{sel.city}</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Top Persona</CardTitle></CardHeader><CardContent><p className="text-lg font-bold text-indigo-600">{sel.top_persona}</p><div className="space-y-1 mt-2">{Object.entries(sel.personas || {}).map(([k, v]) => (<div key={k} className="flex justify-between text-xs"><span className="text-slate-600">{k}</span><span className="font-bold">{v}</span></div>))}</div></CardContent></Card>
            <Card className="border-green-200"><CardHeader className="pb-2"><CardTitle className="text-sm text-green-700">Top High Factors</CardTitle></CardHeader><CardContent><div className="space-y-1">{sel.top_high?.map((f) => (<div key={f.key} className="flex justify-between text-xs"><span className="text-slate-700">{f.name}</span><span className="font-bold text-green-700">{f.count}</span></div>))}</div></CardContent></Card>
            <Card className="border-red-200"><CardHeader className="pb-2"><CardTitle className="text-sm text-red-700">Top Eliminated</CardTitle></CardHeader><CardContent><div className="space-y-1">{sel.top_elim?.map((f) => (<div key={f.key} className="flex justify-between text-xs"><span className="text-slate-700">{f.name}</span><span className="font-bold text-red-700">{f.count}</span></div>))}</div></CardContent></Card>
          </div>
          {Object.keys(sel.budgets || {}).length > 0 && (
            <Card><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Wallet className="w-4 h-4" /> Budget Distribution</CardTitle></CardHeader><CardContent>
              <div className="flex gap-2 flex-wrap">{Object.entries(sel.budgets).map(([k, v]) => (<div key={k} className="px-3 py-1.5 rounded-lg bg-indigo-50 border border-indigo-100"><p className="text-xs font-medium text-indigo-800">{k}</p><p className="text-lg font-bold text-indigo-600">{v}</p></div>))}</div>
            </CardContent></Card>
          )}
        </div>
      )}
    </div>
  );
};

// ============ TAB: RECOMMENDATIONS ============
const RecommendationsTab = ({ personas, city }) => {
  const [selP, setSelP] = useState("");
  const [selProj, setSelProj] = useState("");
  const [recs, setRecs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  useEffect(() => { axios.get(`${API}/mhm/dashboard/project-fit`).then((r) => setProjects(r.data?.projects?.map((p) => p.name) || [])).catch(() => {}); }, []);
  const generate = async () => {
    setLoading(true);
    try { const r = await axios.post(`${API}/mhm/dashboard/recommendations`, { persona: selP || null, project: selProj || null }); setRecs(r.data); } catch { toast.error("Need more MhM data"); } finally { setLoading(false); }
  };
  return (
    <div className="space-y-4">
      <Card><CardContent className="p-4"><p className="text-sm font-medium mb-3">Generate AI recommendations for a persona and/or project</p>
        <div className="flex gap-3 items-end flex-wrap">
          <div><label className="text-xs text-slate-500 block mb-1">Persona</label><select className="border rounded-lg px-3 py-2 text-sm" value={selP} onChange={(e) => setSelP(e.target.value)}><option value="">All</option>{personas?.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}</select></div>
          <div><label className="text-xs text-slate-500 block mb-1">Project</label><select className="border rounded-lg px-3 py-2 text-sm" value={selProj} onChange={(e) => setSelProj(e.target.value)}><option value="">None</option>{projects.map((p) => <option key={p} value={p}>{p}</option>)}</select></div>
          <Button onClick={generate} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700">{loading ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" />Generating...</> : <><Lightbulb className="w-4 h-4 mr-1" />Generate</>}</Button>
        </div>
      </CardContent></Card>
      {recs && (
        <div className="grid md:grid-cols-2 gap-4">
          {recs.buyer_summary?.length > 0 && <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Buyer Summary</CardTitle></CardHeader><CardContent><ul className="space-y-1">{recs.buyer_summary.map((b, i) => <li key={i} className="text-xs text-slate-700 flex gap-2"><span className="text-blue-500">-</span>{b}</li>)}</ul></CardContent></Card>}
          {recs.top_messaging_angles?.length > 0 && <Card className="border-green-200"><CardHeader className="pb-2"><CardTitle className="text-sm text-green-700 flex items-center gap-1"><Lightbulb className="w-4 h-4" /> Messaging</CardTitle></CardHeader><CardContent><ul className="space-y-1">{recs.top_messaging_angles.map((t, i) => <li key={i} className="text-xs text-green-800">+ {t}</li>)}</ul></CardContent></Card>}
          {recs.do_not_overpitch?.length > 0 && <Card className="border-red-200"><CardHeader className="pb-2"><CardTitle className="text-sm text-red-700 flex items-center gap-1"><XCircle className="w-4 h-4" /> Don't Overpitch</CardTitle></CardHeader><CardContent><ul className="space-y-1">{recs.do_not_overpitch.map((d, i) => <li key={i} className="text-xs text-red-800">- {d}</li>)}</ul></CardContent></Card>}
          {recs.likely_objections?.length > 0 && <Card className="border-amber-200"><CardHeader className="pb-2"><CardTitle className="text-sm text-amber-700 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Objections</CardTitle></CardHeader><CardContent><ul className="space-y-1">{recs.likely_objections.map((o, i) => <li key={i} className="text-xs text-amber-800">- {o}</li>)}</ul></CardContent></Card>}
          {recs.positioning_strategy && <Card className="md:col-span-2 bg-gradient-to-r from-slate-900 to-indigo-900 text-white"><CardHeader className="pb-2"><CardTitle className="text-sm text-slate-200">Positioning</CardTitle></CardHeader><CardContent><p className="text-sm text-slate-100 leading-relaxed">{recs.positioning_strategy}</p></CardContent></Card>}
        </div>
      )}
    </div>
  );
};

// ============ MAIN DASHBOARD ============
const MhMInsightsPage = () => {
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async (city) => {
    setLoading(true);
    try {
      const params = city ? `?city=${city}` : "";
      const [ovRes, cfgRes, trendRes, funnelRes, cityRes] = await Promise.all([
        axios.get(`${API}/mhm/dashboard/overview${params}`),
        axios.get(`${API}/mhm/config`),
        axios.get(`${API}/mhm/dashboard/trends${params}`),
        axios.get(`${API}/mhm/dashboard/funnel${params}`),
        axios.get(`${API}/mhm/dashboard/cities`),
      ]);
      setOverview(ovRes.data);
      setPersonas(cfgRes.data?.personas || []);
      setTrends(trendRes.data);
      setFunnel(funnelRes.data);
      setCities(cityRes.data?.city_list || []);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(""); }, [loadData]);

  const handleCityChange = (city) => {
    setSelectedCity(city);
    loadData(city);
  };

  if (loading && !overview) {
    return <Layout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" /></div></Layout>;
  }

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-4" data-testid="mhm-dashboard">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">MhM Strategic Dashboard</h1>
            <p className="text-sm text-slate-500">Buyer priority intelligence from MyHomeMatrix</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Global City Filter */}
            <div className="flex items-center gap-1.5 bg-white border rounded-lg px-3 py-1.5">
              <Filter className="w-3.5 h-3.5 text-slate-400" />
              <select className="text-sm border-0 outline-none bg-transparent pr-2" value={selectedCity} onChange={(e) => handleCityChange(e.target.value)} data-testid="city-filter">
                <option value="">All Cities</option>
                {cities.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {overview && <Badge className="bg-indigo-100 text-indigo-700">{overview.total_profiles} profiles</Badge>}
          </div>
        </div>

        <Tabs defaultValue="overview">
          <TabsList className="bg-slate-100 flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
            <TabsTrigger value="persona" className="text-xs">Persona</TabsTrigger>
            <TabsTrigger value="factors" className="text-xs">Factors</TabsTrigger>
            <TabsTrigger value="project-fit" className="text-xs">Project Fit</TabsTrigger>
            <TabsTrigger value="tradeoffs" className="text-xs">Tradeoff Lab</TabsTrigger>
            <TabsTrigger value="cities" className="text-xs">City Analytics</TabsTrigger>
            <TabsTrigger value="recommendations" className="text-xs">Recommendations</TabsTrigger>
          </TabsList>

          <TabsContent value="overview"><OverviewTab data={overview} trends={trends} funnel={funnel} /></TabsContent>
          <TabsContent value="persona"><PersonaTab personas={personas} city={selectedCity} /></TabsContent>
          <TabsContent value="factors"><FactorTab city={selectedCity} /></TabsContent>
          <TabsContent value="project-fit"><ProjectFitTab city={selectedCity} /></TabsContent>
          <TabsContent value="tradeoffs"><TradeoffTab city={selectedCity} /></TabsContent>
          <TabsContent value="cities"><CityTab /></TabsContent>
          <TabsContent value="recommendations"><RecommendationsTab personas={personas} city={selectedCity} /></TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default MhMInsightsPage;
